import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { generatePath, useNavigate } from 'react-router-dom';

import { availabilityTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { GroupsSkeleton } from 'us-react-components/dist/Groups/GroupsSkeleton';
import {
  Affiliation,
  AffiliationService,
  FavoriteSitterService, LocalGroup,
  PagedResponse,
  PagedResponseData,
  ParentAffiliationService,
  ParentSearchBar,
  School,
  Sitter,
  SitterService,
  UserGeocode
} from 'us-web-services';
import { useDependencies } from '../../../../../dependencies';
import { Routes } from '../../../../../Routes';
import { GroupsNullState } from './GroupsNullState';
import { GroupSitters } from './GroupSitters';

interface Props {
  newSearch: ParentSearchBar;
  geocode: UserGeocode;
  getWillSitParams(search: ParentSearchBar): object;
}

export const MyGroups = observer((props: Props): JSX.Element => {
  const { stores } = useDependencies();
  const navigate = useNavigate();
  const blankResponse: PagedResponseData<Sitter> = {
    data: [],
    meta: { page: 1, size: 0 },
  };
  const [friendSitters, setFriendSitters] = useState<PagedResponseData<Sitter>>(blankResponse);
  const [schoolSitters, setSchoolSitters] = useState<PagedResponseData<Sitter>>(blankResponse);
  const [affiliationSitters, setAffiliationSitters] = useState<PagedResponseData<Sitter>>(blankResponse);
  const [localGroupSitters, setLocalGroupSitters] = useState<PagedResponseData<Sitter>>(blankResponse);
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [hasFriendSitters, setHasFriendSitters] = useState(false);
  const [hasSchoolSitters, setHasSchoolSitters] = useState(false);
  const [hasAffiliationSitters, setHasAffiliationSitters] = useState(false);
  const [hasLocalGroupSitters, setHasLocalGroupSitters] = useState(false);
  const [loadingFriendSitters, setLoadingFriendSitters] = useState(true);
  const [loadingSchoolSitters, setLoadingSchoolSitters] = useState(true);
  const [loadingAffiliationSitters, setLoadingAffiliationSitters] = useState(true);
  const [loadingLocalGroupSitters, setLoadingLocalGroupSitters] = useState(true);

  const [school, setSchool] = useState<School>({});
  const [affiliation, setAffiliation] = useState<Affiliation>({});
  const [localGroup, setLocalGroup] = useState<LocalGroup>({});
  const [friendParams, setFriendParams] = useState<object>({});
  const [schoolParams, setSchoolParams] = useState<object>({});
  const [affiliationParams, setAffiliationParams] = useState<object>({});
  const [localGroupParams, setLocalGroupParams] = useState<object>({});

  const { user } = stores.user;
  const { parent } = stores.parent;
  const { geocode, newSearch, getWillSitParams } = props;

  const setFavorite = async (isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void> => {
    const config = {
      parentId: user.id,
      sitterId,
    };

    if (isFavorited) {
      await FavoriteSitterService.create(config);
    } else {
      await FavoriteSitterService.delete(favoriteId);
    }
  }

  const getSitters = async (params: unknown): Promise<PagedResponse<Sitter>> => {
    const config = {
      params,
    };

    return SitterService.getByFilter(config);
  }

  const getFriendSitters = async (params: object): Promise<void> => {
    const response = (await getSitters(params)).data;
    setFriendParams(params);
    setFriendSitters(response);
    setHasFriendSitters(response.meta.size > 0);
    setLoadingFriendSitters(false);
  }

  const getSchoolSitters = async (params: object): Promise<void> => {
    const response = (await getSitters(params)).data;
    setSchoolParams(params);
    setSchoolSitters(response);
    setHasSchoolSitters(response.meta.size > 0);
    setLoadingSchoolSitters(false);
  }

  const getAffiliationSitters = async (params: object): Promise<void> => {
    const response = (await getSitters(params)).data;
    setAffiliationParams(params);
    setAffiliationSitters(response);
    setHasAffiliationSitters(response.meta.size > 0);
    setLoadingAffiliationSitters(false);
  }

  const getLocalGroupSitters = async (params: object): Promise<void> => {
    const response = (await getSitters(params)).data;
    setLocalGroupParams(params);
    setLocalGroupSitters(response);
    setHasLocalGroupSitters(response.meta.size > 0);
    setLoadingLocalGroupSitters(false);
  }

  const getAllAffiliations = async (): Promise<void> => {
    const parentAffConfig = {
      params: {
        parentId: user.id,
      }
    };

    const parentAffResponse = (await ParentAffiliationService.getByFilter(parentAffConfig)).data;
    const parentAffIds = parentAffResponse.data.map(aff => aff.affiliation?.id);

    const config = {
      params: {
        city: geocode.city,
        state: geocode.state,
        parentId: user.id,
        recommendations: true,
        ignoreIds: parentAffIds.toString(),
      }
    }

    const response = (await AffiliationService.getByFilter(config)).data;
    setAffiliations(response.data);
  }

  const navigateToExploreGroupsPage = async (): Promise<void> => {
    const parentAffConfig = {
      params: {
        parentId: user.id,
      }
    };

    await ParentAffiliationService.getByFilter(parentAffConfig);
    navigate(generatePath(Routes.parent.explore, { id: user.id }));
  }

  useEffect(() => {
    if (!geocode.zip) {
      return;
    }

    const originalParams = {
      limit: 4,
      userId: user.id,
      parentId: user.id,
      city: geocode?.city ?? parent?.address?.city,
      zip: newSearch.zip ?? parent?.address?.zip,
      state: geocode?.state ?? parent?.address?.state,
      distance: newSearch.distance ?? 25,
      latitude: geocode?.latitude ?? parent?.address?.latitude,
      longitude: geocode?.longitude ?? parent?.address?.longitude,
      numberOfChildren: parent?.kids?.length || 1,
      verticalTypeId: newSearch.vertical?.id ?? bookingVerticalType.CHILD_CARE,
      availabilityType: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.type ?? 'All',
      searchTypeCode: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.code ?? 'ALL',
      excludeFavorites: true,
    };

    const additionalParams = getWillSitParams(newSearch);
    const params = {
      ...originalParams,
      ...additionalParams,
    };

    const friendRequestParams = {
      ...params,
      friendsSitters: true,
    };

    getFriendSitters(friendRequestParams);

    if (parent?.schools?.length) {
      const parentSchool = parent?.schools[0];
      const schoolRequestParams = {
        ...params,
        schoolId: parentSchool?.school?.id,
      };
      getSchoolSitters(schoolRequestParams);
      setSchool(parentSchool?.school || {});
    } else {
      setLoadingSchoolSitters(false);
    }

    if (parent?.affiliations?.length) {
      const parentAffiliation = parent?.affiliations[0];
      const affiliationRequestParams = {
        ...params,
        affiliationId: parentAffiliation?.affiliation?.id,
      };
      getAffiliationSitters(affiliationRequestParams);
      setAffiliation(parentAffiliation?.affiliation || {});
    } else {
      setLoadingAffiliationSitters(false);
    }

    if (parent?.localGroups?.length) {
      const parentLocalGroup = parent?.localGroups[0];
      const localGroupRequestParams = {
        ...params,
        localGroupId: parentLocalGroup.localGroup?.id,
      };
      getLocalGroupSitters(localGroupRequestParams);
      setLocalGroup(parentLocalGroup?.localGroup || {});
    } else {
      setLoadingLocalGroupSitters(false);
    }

    getAllAffiliations();
  }, [geocode, newSearch]);

  if (loadingFriendSitters || loadingSchoolSitters || loadingAffiliationSitters || loadingLocalGroupSitters) {
    return (
      <div className='bg-transparent mb-10'>
        <GroupsSkeleton />
      </div>
    );
  }

  return (
    <div className='bg-transparent'>
      {hasFriendSitters || hasSchoolSitters || hasAffiliationSitters || hasLocalGroupSitters ? (
        <>
          {hasFriendSitters && <GroupSitters sitters={friendSitters} params={friendParams} hideExperience={false} setFavorite={setFavorite} />}
          {hasSchoolSitters && <GroupSitters school={school} params={schoolParams}
            sitters={schoolSitters} hideExperience={false} setFavorite={setFavorite} />}
          {hasAffiliationSitters && <GroupSitters affiliation={affiliation} params={affiliationParams}
            sitters={affiliationSitters} hideExperience={false} setFavorite={setFavorite} />}
          {hasLocalGroupSitters && <GroupSitters localGroup={localGroup} params={localGroupParams}
            sitters={localGroupSitters} hideExperience={false} setFavorite={setFavorite} />}
        </>
      ) : (
        <GroupsNullState affiliations={affiliations} navigateToExploreGroupsPage={navigateToExploreGroupsPage} />
      )}
    </div>
  );
});
